import _ from 'underscore';

import {
  ActionHelpers, standardActionErrorMsg, StandardActionToastMessages
} from '~commonInfra/contentActions/ActionHelpers';
import { IErrorItem } from '~commonInfra/contentActions/MultipleItemResult';
import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { L10n } from '~commonInfra/L10n/L10n';
import { Set } from '~commonInfra/utils/Set';
import { CompositeSessionInfoSelectors, SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Formatters, Messages } from '~src/codegen/Localize';
import { CollectionMapper } from '~src/Collections/mappers/CollectionMapper';
import { CollectionsResourceQuery, UpdateCollectionData } from '~src/Collections/ResourceQuery/CollectionsResourceQuery';
import { CollectionErrors } from '~src/Collections/services/CollectionErrors';
import { UpdateCollectionsResponseData } from '~src/Collections/space/CollectionModels';
import { ICollectionInfo } from '~src/Collections/space/ICollectionInfo';
import { IDataRoleInfo } from '~src/dataRole/models/IDataRoleInfo';
import { DataRoles } from '~src/dataRole/resources/Dataroles';
import { DataRoleErrors } from '~src/dataRole/services/DataRoleErrors';
import { IDataSourceBase } from '~src/datasource/models/IDataSourceInfo';
import { DataSources } from '~src/datasource/resources/Datasources';
import { DataSourceErrors } from '~src/datasource/services/DataSourceErrors';
import { IDataAlertBase } from '~src/dda/models/IDataAlertInfo';
import { DataAlerts } from '~src/dda/resources/DataAlerts';
import { DataAlertErrors } from '~src/dda/services/DataAlertErrors';
import { FlowListItemInfo } from '~src/flow/models/FlowInfo';
import { Flows } from '~src/flow/resources/Flows';
import { FlowErrors } from '~src/flow/services/FlowErrors';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { ILensInfo } from '~src/lens/models/ILensInfo';
import { Lenses } from '~src/lens/resources/Lenses';
import { LensErrors } from '~src/lens/services/LensErrors';
import { IMetricInfo } from '~src/metrics/models/IMetricInfo';
import { Metrics } from '~src/metrics/resources/Metrics';
import { MetricErrors } from '~src/metrics/services/MetricErrors';
import { ProjectErrors } from '~src/projects/actions/ProjectErrors';
import { IProjectBase } from '~src/projects/models/IProjectInfo';
import { Projects } from '~src/projects/resources/Projects';
import { IPublishedConnectionInfo } from '~src/publishedConnection/models/IPublishedConnectionInfo';
import { PublishedConnections } from '~src/publishedConnection/resources/PublishedConnections';
import { PublishedConnectionErrors } from '~src/publishedConnection/services/PublishedConnectionErrors';
import {
  SetOwnerDialogBody, SetOwnerDialogBodyProps, SetOwnerDialogState
} from '~src/spacesPlatform/components/content/SetOwnerDialog';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { SearchSuggestionsService } from '~src/spacesPlatform/services/SearchSuggestionsService';
import { IUserName } from '~src/userManagement/users/serverApi/types/IUser';
import { CustomViews } from '~src/views/ServerApi/requests/CustomViews/CustomViews';
import { ICustomViewBase, ICustomViewInfo } from '~src/views/ServerApi/requests/CustomViews/ICustomViewInfo';
import { CustomViewErrors } from '~src/views/services/customViews/CustomViewErrors';
import { IWorkbookBase } from '~src/workbook/models/IWorkbookInfo';
import { Workbooks } from '~src/workbook/resources/Workbooks';
import { WorkbookErrors } from '~src/workbook/services/WorkbookErrors';

export namespace ChangeContentOwnerAction {

  export function confirmChangeOwner(content: IContentInfo[]): Promise<IResult> {
    const isAdmin = CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(getAppStore().getState());
    const userId = SessionUserSelectors.sessionUserId(getAppStore().getState());
    const isOwner = _.some(content, (item) => item.owner && item.owner.id === userId);
    const containsOneWorkbook = !_.isUndefined(content.find((info) => info.contentType === 'workbook'));
    const count = content.length;
    const modalInstance = openReactActionDialog<SetOwnerDialogBodyProps, SetOwnerDialogState, IResult>({
      title: Messages.changeOwnerDialogTitle(),
      bodyComponent: SetOwnerDialogBody,
      initialBodyProps: {
        prompt: getPromptForContent(content),
        showCredentialsWarning: containsOneWorkbook,
        isAdmin,
        isOwner,
        getQuery: (matchText: string) => getQuery(content, matchText)
      },
      initialDialogState: { targetOwner: null },
      actionName: Messages.changeOwnerConfirm({ COUNT: count, COUNT_LOC: Formatters.formatCount(count) }),
      action: (_bodyProps: SetOwnerDialogBodyProps, dialogState: SetOwnerDialogState) => {
        return setOwnerForContent(content, dialogState.targetOwner).then((results: IResult[]) => {
          return ResultHelper.combineResults(results);
        });
      },
      actionDisabled: (_bodyProps: SetOwnerDialogBodyProps, dialogState: SetOwnerDialogState) => dialogState.targetOwner == null,
      dialogWidth: TabDialogWidth.width500px,
      dismissDialogOnActionFailed: false,
    });

    return modalInstance.result;
  }

  export function getQuery(content: IContentInfo[], matchText: string) {
    const containsProjects = content.find(i => i.contentType === 'project') !== undefined;
    const containsFlows = content.find(i => i.contentType === 'flow') !== undefined;
    const containsMetrics = content.find(i => i.contentType === 'metric') !== undefined;
    const containsDatasources = content.find(i => i.contentType === 'datasource') !== undefined;

    if (containsFlows) {
      // for security reasons, only allow user to change flow ownership to themselves see TFSID: 1204099
      return SearchSuggestionsService.currentLoggedInUser(matchText);
    }

    let users = SearchSuggestionsService.users(matchText);
    if (!containsMetrics && !containsProjects && !containsDatasources) {
      return SearchSuggestionsService.addWithoutGuestFilter(users);
    }

    if (containsMetrics) {
      users = SearchSuggestionsService.addMetricOwnerFilter(users);
    }

    if (containsProjects) {
      users = SearchSuggestionsService.addProjectOwnerFilter(users);
    }

    if (containsDatasources) {
      users = SearchSuggestionsService.addDatasourceOwnerFilter(users);
    }
    return users;
  }

  // public for testing only!  Calling this bypasses the confirm dialog UI.
  export function setOwnerForContent(content: IContentInfo[], owner: IUserName) {
    const projects = Set.fromIdItemArray(content.filter(i => i.contentType === 'project'));
    const workbooks = Set.fromIdItemArray(content.filter(i => i.contentType === 'workbook'));
    const datasources = Set.fromIdItemArray(content.filter(i => i.contentType === 'datasource'));
    const flows = Set.fromIdItemArray(content.filter(i => i.contentType === 'flow'));
    const dataroles = Set.fromIdItemArray(content.filter(i => i.contentType === 'dataRole'));
    const lenses = Set.fromIdItemArray(content.filter(i => i.contentType === 'lens'));
    const alerts = Set.fromIdItemArray(content.filter(i => i.contentType === 'dataalert'));
    const metrics = Set.fromIdItemArray(content.filter(i => i.contentType === 'metric'));
    const publishedConnections = Set.fromIdItemArray(content.filter(i => i.contentType === 'virtualConnection'));
    const collections = Set.fromIdItemArray(content.filter(i => i.contentType === 'collection'));
    const customViews = Set.fromIdItemArray(content.filter(i => i.contentType === 'customView'));

    return Promise.all([
      setOwnerForProjects(projects, owner),
      setOwnerForWorkbooks(workbooks, owner),
      setOwnerForDatasources(datasources, owner),
      setOwnerForFlows(flows, owner),
      setOwnerForDataRoles(dataroles, owner),
      setOwnerForLenses(lenses, owner),
      setOwnerForDataAlerts(alerts, owner),
      setOwnerForMetrics(metrics, owner),
      setOwnerForPublishedConnections(publishedConnections, owner),
      setOwnerForCollections(collections, owner),
      setOwnerForCustomViews(customViews, owner),
    ]);
  }

  function setOwnerForProjects(projects: Set<IContentInfo>, owner: IUserName): Promise<IResult> {
    if (projects.size() < 1) {
      return Promise.resolve(null);
    }

    return Projects.setOwner(projects.keys(), owner.id).then((result) => {
      return ActionHelpers.displayStandardToastsForResult(
        projects,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerProjectsSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerProjectsError),
        },
        (errorItem: IErrorItem<IProjectBase>) => ProjectErrors.errorDetailsFor(errorItem),
      );
    });
  }

  function setOwnerForMetrics(metrics: Set<IContentInfo>, owner: IUserName): Promise<IResult> {
    if (metrics.size() < 1) {
      return Promise.resolve(null);
    }

    return Metrics.setOwner(metrics.keys(), owner.id).then((result) => {
      return ActionHelpers.displayStandardToastsForResult(
        metrics,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerForMetricsSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerForMetricsError),
        },
        (errorItem: IErrorItem<IMetricInfo>) => MetricErrors.errorDetailsFor(errorItem),
      );
    });
  }

  function setOwnerForPublishedConnections(publishedConnections: Set<IContentInfo>, owner: IUserName): Promise<IResult> {
    if (publishedConnections.size() < 1) {
      return Promise.resolve(null);
    }

    return PublishedConnections.setOwner(publishedConnections.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        publishedConnections,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerPublishedConnectionsSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerPublishedConnectionsError),
        },
        (errorItem: IErrorItem<IPublishedConnectionInfo>) => PublishedConnectionErrors.errorDetailsFor(errorItem),
      );
    });
  }

  function setOwnerForWorkbooks(workbooks: Set<IContentInfo>, owner: IUserName): Promise<IResult> {
    if (workbooks.size() < 1) {
      return Promise.resolve(null);
    }

    return Workbooks.setOwner(workbooks.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        workbooks,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerWorkbooksSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerWorkbooksError),
        },
        (errorItem: IErrorItem<IWorkbookBase>) => WorkbookErrors.errorDetailsFor(errorItem),
      );
    });
  }

  function setOwnerForDatasources(datasources: Set<IContentInfo>, owner: IUserName): Promise<IResult> {
    if (datasources.size() < 1) {
      return Promise.resolve(null);
    }

    return DataSources.setOwner(datasources.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        datasources,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerDatasourcesSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerDatasourcesError),
        },
        (errorItem: IErrorItem<IDataSourceBase>) => DataSourceErrors.errorDetailsFor(errorItem),
      );
    });
  }

  function setOwnerForFlows(flows: Set<IContentInfo>, owner: IUserName): Promise<IResult> {
    if (flows.size() < 1) {
      return Promise.resolve(null);
    }

    return Flows.setOwner(flows.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        flows,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerFlowsSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerFlowsError),
        },
        (errorItem: IErrorItem<FlowListItemInfo>) => FlowErrors.errorDetailsFor(errorItem),
      );
    });
  }

  function setOwnerForDataRoles(dataroles: Set<IContentInfo>, owner: IUserName): Promise<IResult> {
    if (dataroles.size() < 1) {
      return Promise.resolve(null);
    }

    return DataRoles.setOwner(dataroles.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        dataroles,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerDataRolesSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerDataRolesError),
        },
        (errorItem: IErrorItem<IDataRoleInfo>) => DataRoleErrors.errorDetailsFor(errorItem),
      );
    });
  }

  function setOwnerForLenses(lenses: Set<IContentInfo>, owner: IUserName): Promise<IResult> {
    if (lenses.size() < 1) {
      return Promise.resolve(null);
    }

    return Lenses.setOwner(lenses.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        lenses,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerLensesSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerLensesError),
        },
        (errorItem: IErrorItem<ILensInfo>) => LensErrors.errorDetailsFor(errorItem),
      );
    });
  }

  function setOwnerForDataAlerts(alerts: Set<IContentInfo>, owner: IUserName): Promise<IResult> {
    if (alerts.size() < 1) {
      return Promise.resolve(null);
    }

    return DataAlerts.setOwner(alerts.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult<IDataAlertBase>(
        alerts as Set<IDataAlertBase>,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerDataAlertsSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerDataAlertsError),
        },
        (errorItem: IErrorItem<IDataAlertBase>) => DataAlertErrors.errorDetailsFor(errorItem),
      );
    });
  }

  function setOwnerForCollections(collections: Set<IContentInfo>, owner: IUserName): Promise<IResult> {
    if (collections.size() < 1) {
      return Promise.resolve(null);
    }

    const updateModels: UpdateCollectionData[] = collections.map((collection) => {
      return {
        luid: collection.luid,
        ownerLuid: owner.luid,
      };
    });

    const promise = new CollectionsResourceQuery().update(updateModels).then(
      (result: UpdateCollectionsResponseData): IResult => {
        return CollectionMapper.mapUpdateCollectionsResponseDataToIResult(result, collections);
      }).catch((error: any) => {
        return { errors: [{ code: error.data.errorCode, id: undefined }] } as IResult;
      });

    return promise.then((result) => {
      return ActionHelpers.displayStandardToastsForResult<ICollectionInfo>(
        collections as Set<ICollectionInfo>,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.CollectionsChangeOwnerSuccess, owner),
          error: standardActionErrorMsg(Messages.CollectionsChangeOwnerError),
        },
        (errorItem: IErrorItem<ICollectionInfo>) => CollectionErrors.errorDetailsFor(errorItem)
      );
    });
  }

  function setOwnerForCustomViews(customViews: Set<IContentInfo>, owner: IUserName): Promise<IResult> {
    if (customViews.size() < 1) {
      return Promise.resolve(null);
    }

    return CustomViews.setOwner(customViews.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult<ICustomViewBase>(
        customViews as Set<ICustomViewBase>,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerCustomViewsSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerCustomViewsError),
        },
        (errorItem: IErrorItem<ICustomViewInfo>) => CustomViewErrors.errorDetailsFor(errorItem),
      );
    });
  }
}

function changeOwnerSuccessTranslation(
  messagesFn: (a: { ITEM_COUNT: number, ITEM_COUNT_LOC: string, OWNER_NAME: string }) => string,
  owner: IUserName,
): StandardActionToastMessages<unknown>['success'] {
  return ({ successCount }) => messagesFn({
    ITEM_COUNT: successCount,
    ITEM_COUNT_LOC: Formatters.formatCount(successCount),
    OWNER_NAME: owner.displayName,
  });
}

function getPromptForContent(content: IContentInfo[]): string {
  if (content.length === 1) {
    const item = content[0];
    return Messages.changeOwnerContentItemPrompt({ NAME: L10n.elideIfLong(item.name) });
  } else {
    return Messages.changeOwnerContentItemsPrompt({ COUNT_LOC: Formatters.formatCount(content.length) });
  }
}

export type ChangeContentOwnerAction = typeof ChangeContentOwnerAction;
